import { Table } from 'react-bootstrap';
import { Table as ReactTable, flexRender } from '@tanstack/react-table';
import SimpleBarReact from 'simplebar-react';

type AdvanceTableProps = {
  /**
   * The caller writes `<AdvanceTable table />` (a boolean marker) so
   * AdvanceTableWrapper can detect this child and, via cloneElement, replace the
   * marker with the actual @tanstack/react-table instance at runtime. Hence the
   * prop accepts both the boolean marker and the injected instance.
   */
  table?: boolean | ReactTable<object>;
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string;
  tableProps?: React.ComponentProps<typeof Table>;
};

const AdvanceTable = ({
  table,
  headerClassName,
  bodyClassName,
  rowClassName,
  tableProps,
}: AdvanceTableProps) => {
  // Until AdvanceTableWrapper swaps the boolean marker for the real instance,
  // there is nothing to render.
  if (!table || typeof table === 'boolean') {
    return null;
  }

  return (
    <SimpleBarReact>
      <Table {...tableProps}>
        <thead className={headerClassName}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                // headerProps is a custom column option carried on columnDef.meta
                const headerProps = (
                  header.column.columnDef.meta as
                    | { headerProps?: React.ThHTMLAttributes<HTMLTableCellElement> }
                    | undefined
                )?.headerProps;
                return (
                  <th
                    key={header.id}
                    {...headerProps}
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {canSort ? (
                      sorted ? (
                        sorted === 'desc' ? (
                          <span className="sort desc" />
                        ) : (
                          <span className="sort asc" />
                        )
                      ) : (
                        <span className="sort" />
                      )
                    ) : (
                      ''
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className={bodyClassName}>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={rowClassName}>
              {row.getVisibleCells().map((cell) => {
                // cellProps is a custom column option carried on columnDef.meta
                const cellProps = (
                  cell.column.columnDef.meta as
                    | { cellProps?: React.TdHTMLAttributes<HTMLTableCellElement> }
                    | undefined
                )?.cellProps;
                return (
                  <td key={cell.id} {...cellProps}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </SimpleBarReact>
  );
};

export default AdvanceTable;
